import React from "react";
import { motion } from "framer-motion";
import { Logo } from "./Logo";
import { HOME } from "@/constants/testIds";

const CREED_LINES = [
  "I won't quit.",
  "I don't fold.",
  "I fight — even when the fight's inside me.",
  "Pain is my training partner.",
  "Failure is my teacher.",
  "Faith is my fuel.",
  "When the weight crushes me — I rise again.",
  "When my body breaks — my spirit keeps going.",
  "Because He never gave up on me,",
  "so I'll never give up on the grind.",
  "Every rep is a reminder —",
  "God's strength is greater than my struggle.",
  "I was built for the battle,",
  "forged by fire,",
  "and powered by faith.",
  "I don't stop when I'm tired.",
  "I stop when I'm done —",
  "and God says \u201Cwell done.\u201D",
];

const SERENITY_LINES = [
  "God, grant me the serenity",
  "to accept the things I cannot change,",
  "courage to change the things I can,",
  "and wisdom to know the difference.",
];

export const Creed = () => {
  return (
    <section
      id="creed"
      data-testid={HOME.creed}
      className="relative bg-black text-white py-32 md:py-44 overflow-hidden"
    >
      {/* Faded oversized logo backdrop */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.05] pointer-events-none">
        <Logo showWordmark={false} className="w-[95vw] h-auto max-w-[1400px]" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center mb-14 md:mb-20"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-10 h-px bg-silver-300" />
            <span className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
              §04 — THE CREED
            </span>
            <span className="w-10 h-px bg-silver-300" />
          </div>
          <h2 className="font-mono text-white text-sm md:text-[15px] tracking-[0.38em] mb-6">
            MBS OVERLOAD CREED
          </h2>
          <div className="font-serif italic text-[9vw] md:text-[4.4vw] leading-[1.02] text-silver-100 tracking-[-0.02em]">
            God <span className="not-italic text-white">First.</span>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="mt-8 md:mt-10 mx-auto max-w-2xl text-white/65 text-[14px] md:text-[15px] leading-[1.8]"
          >
            It&apos;s raw, relentless — built for warriors who bleed discipline and
            put God first no matter how heavy life hits.
          </motion.p>
        </motion.div>

        {/* Creed body */}
        <ol className="relative border-l border-white/10 pl-8 md:pl-14 md:mx-8 space-y-3 md:space-y-4">
          {CREED_LINES.map((line, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -18, filter: "blur(3px)" }}
              whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.7,
                delay: Math.min(i * 0.06, 0.7),
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative font-serif text-white text-[5.6vw] md:text-[1.85vw] leading-[1.35] tracking-[-0.005em]"
            >
              <span className="absolute -left-8 md:-left-14 top-2 md:top-3 font-mono text-[10px] tracking-[0.3em] text-silver-300/70 w-8 md:w-14 text-right pr-2">
                {String(i + 1).padStart(2, "0")}
              </span>
              {line}
            </motion.li>
          ))}
        </ol>

        {/* Divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20 md:mt-24 mx-auto max-w-[520px] flex items-center gap-4 text-silver-200/60 origin-center"
        >
          <span className="flex-1 h-px bg-silver-300/60" />
          <span className="font-serif italic text-silver-200 text-sm md:text-base whitespace-nowrap">
            The Serenity Prayer
          </span>
          <span className="flex-1 h-px bg-silver-300/60" />
        </motion.div>

        {/* Serenity Prayer */}
        <div className="mt-10 md:mt-14 text-center space-y-2 md:space-y-3">
          {SERENITY_LINES.map((l, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.9,
                delay: i * 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="font-serif italic text-silver-100 text-[5vw] md:text-[1.8vw] leading-[1.5] tracking-[-0.01em]"
            >
              {l}
            </motion.p>
          ))}
        </div>

        {/* Signature */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.9 }}
          className="mt-16 md:mt-20 flex flex-col items-center gap-3"
        >
          <span className="w-px h-12 bg-silver-100/60" />
          <span className="font-mono text-[10px] tracking-[0.4em] text-silver-300">
            — SIGNED, THE NUMBERED
          </span>
          <span className="font-serif italic text-white/50 text-sm">
            God first. Grind always.
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Creed;
